import { saveAs } from 'file-saver';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import DarkMenuBar from '../../components/DarkMenuBar/DarkMenuBar';
import FilterSideBar from '../../components/FilterSideBar/FilterSideBar';
import { ProductCardList } from '../../components/ProductCard/ProductCardList';
import { ProductTableList } from '../../components/ProductRow/ProductTableList';
import { useBusinessUnit } from '../../context/BusinessUnitContext';
import { useFilter } from '../../context/FilterContext';
import Product from '../../models/Product';
import { PlatformService } from '../../services/platform.service';
import { ProductService } from '../../services/product.service';
import { AuthService } from '../../services/auth.service';
import './Home.css';
import { DelegateService } from '../../services/delegate.service';
import { KamService } from '../../services/kam.service';
import ExcelJS from 'exceljs';
import { useShoppingCart } from '../../context/ShoppingCartContext';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { DEFAULT_CONGRESS_PRODUCTS } from '../../constants/congressDefaultProducts';

export default function Products() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [products, setProducts] = useState<Product[]>([]);
  const { filters } = useFilter();
  const [isLoading, setIsLoading] = useState(false);
  const [idLanguageUser, setIdLanguageUser] = useState('es');
  const [isDelegate, setIsDelegate] = useState(false);
  const [isKAM, setIsKam] = useState(false);
  const [isCongress, setIsCongress] = useState(false);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { businessUnitKey, businessUnitName } = useBusinessUnit();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const productsCounter = `${products.length} ${t('home.amount_products_label.substring_1')} ${businessUnitKey == null || businessUnitKey == '' ? t('home.amount_products_label.substring_2') : businessUnitName}`;
  const { setQuantity, cartItems, increaseCartQuantity } = useShoppingCart();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    setIsLoading(true);
    checkRole();
    getProducts(searchQuery);
    getAllCatalogue();
    setIsLoading(false);
    console.log('Filters applied:', filters);
  }, [idLanguageUser, businessUnitKey, filters]);

  function checkRole() {
    const role = AuthService.GetRole();
    if (role == 'Delegate') {
      setIsDelegate(true);
    } else if (role == 'KAM') {
      setIsKam(true);
    } else if (role == 'Congress') {
      setIsCongress(true);
    }
  }
  function getAllCatalogue() {
    ProductService.GetProductsByLanguage('congress').then((response) => {
      const responseData = response.data as Product[];
      setAllProducts(responseData);
    });
  }

  function getProducts(query: string) {
    setIdLanguageUser(AuthService.GetLanguageID() ?? 'es');

    ProductService.GetProducts(businessUnitKey ?? '').then((response) => {
      const responseProducts = response.data as Product[];

      const typeFilteredProducts = filters.productType.length
        ? responseProducts.filter((product) => {
            console.log(
              'Filtering by Product Type for product:',
              product.businessUnit,
            );
            console.log('Current Filters:', filters);
            return filters.productType.includes(
              product.productType?.toUpperCase() ?? '',
            );
          })
        : responseProducts;

      const platformFilteredProducts = filters.platform.length
        ? typeFilteredProducts.filter((product) =>
            filters.platform.includes(product.family?.toUpperCase() ?? ''),
          )
        : typeFilteredProducts;

      const languagesFilteredProducts = filters.idLanguage.length
        ? platformFilteredProducts.filter(
            (product) =>
              filters.idLanguage.includes(product.idLanguage ?? '') ||
              product.idLanguage == '',
          )
        : platformFilteredProducts.filter(
            (product) =>
              product.idLanguage == idLanguageUser || product.idLanguage == '',
          );

      const onDemandFilteredProducts = languagesFilteredProducts.filter(
        (product) => {
          console.log('Filtering On Demand for product:', filters);
          if (filters.isOnDemand === undefined) {
            return product;
          }
          return product.isOnDemand === filters.isOnDemand;
        },
      );

      const endOfStockFilteredProducts = onDemandFilteredProducts.filter(
        (product) => {
          console.log('Filtering End of Stock for product:', filters);
          if (filters.endOfStock === undefined) {
            return product;
          } else if (filters.endOfStock === true) {
            return product.isEnabled === 'Agotar';
          } else {
            return product;
          }
        },
      );

      if (query === '') {
        getPlatforms();
        setProducts(sortProductsByPlatform(endOfStockFilteredProducts));
        return;
      } else {
        const searchFilteredProducts = endOfStockFilteredProducts.filter(
          (product) =>
            product.nameProduct.toLowerCase().includes(query.toLowerCase()) ||
            product.code.toLowerCase().includes(query.toLowerCase()),
        );
        query;
        console.log('Filtered Products:', searchFilteredProducts);
        getPlatforms();
        setProducts(sortProductsByPlatform(searchFilteredProducts));
      }
    });
  }

  function getPlatforms() {
    PlatformService.GetPlatformsOfTheProducts().then((response) => {
      const responseData = response.data as string[];
      setPlatforms(responseData);
    });
  }

  function sortProductsByPlatform(products: Product[]) {
    const platformOrder = platforms.map((p) => p.toUpperCase());

    const sortedProducts = products.sort((a, b) => {
      const platformA = a.family?.toUpperCase() ?? '';
      const platformB = b.family?.toUpperCase() ?? '';

      const indexA = platformOrder.indexOf(platformA);
      const indexB = platformOrder.indexOf(platformB);

      if (indexA !== -1 && indexB !== -1) {
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        const nameA = a.nameProduct?.toUpperCase() ?? '';
        const nameB = b.nameProduct?.toUpperCase() ?? '';
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      }

      if (indexA !== -1) {
        return -1;
      }

      if (indexB !== -1) {
        return 1;
      }

      if (platformA > platformB) return 1;
      if (platformA < platformB) return -1;

      const nameA = a.nameProduct?.toUpperCase() ?? '';
      const nameB = b.nameProduct?.toUpperCase() ?? '';
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;

      return 0;
    });

    return sortedProducts;
  }

  function downloadProductsToXLSX() {
    ProductService.GetProductsByLanguage(idLanguageUser).then((response) => {
      const responseData = response.data as Product[];
      const sortedData = sortProductsByPlatform(responseData);
      setAllProducts(sortedData);

      const worksheetData = sortedData.map((product) => ({
        [t('home.excel_document.code').toLocaleUpperCase()]: product.code,
        [t('home.excel_document.description').toLocaleUpperCase()]:
          product.nameProduct.toLocaleLowerCase(),
        [t('home.excel_document.business_unit').toLocaleUpperCase()]:
          product.businessUnit?.toLocaleLowerCase() == 'resto'
            ? 'corporativo'
            : product.businessUnit?.toLocaleLowerCase(),
        [t('home.excel_document.platform').toLocaleUpperCase()]:
          product.family?.toLocaleLowerCase(),
        [t('home.excel_document.subplatform').toLocaleUpperCase()]:
          product.subfamily?.toLocaleLowerCase(),
        [t('home.excel_document.product_type').toLocaleUpperCase()]:
          product.productType?.toLocaleLowerCase(),
        [t('home.excel_document.price').toLocaleUpperCase()]:
          `${product.price?.toFixed(2)} €`.toLocaleLowerCase(),
        [t('home.excel_document.is_on_demand.title').toLocaleUpperCase()]:
          product.isOnDemand
            ? t('home.excel_document.is_on_demand.accept').toLocaleLowerCase()
            : t('home.excel_document.is_on_demand.reject').toLocaleLowerCase(),
      }));
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);

      const range = XLSX.utils.decode_range(worksheet['!ref'] || '');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ c: C, r: 0 });
        if (!worksheet[cell_address]) continue;
        worksheet[cell_address].s = {
          font: {
            name: 'Arial',
            sz: 12,
            bold: true,
          },
          alignment: {
            vertical: 'center',
            horizontal: 'center',
          },
        };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      const date = new Date();
      const formattedDate = date.toISOString().slice(0, 10);
      const formattedTime = date.toTimeString().slice(0, 8).replace(/:/g, '');

      saveAs(blob, `Merchandising_${formattedDate}_${formattedTime}.xlsx`);
    });
  }
  const fallbackURL = '/placeholder.jpg';

  const checkImageExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const resolveImageSource = async (code: string): Promise<string> => {
    const pngUrl = `/photos/${code}.png`;
    const jpgUrl = `/photos/${code}.jpg`;

    if (await checkImageExists(pngUrl)) return pngUrl;
    if (await checkImageExists(jpgUrl)) return jpgUrl;
    return fallbackURL;
  };

  async function downloadProductsForCongressExcelJS(products: Product[]) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Products');

    worksheet.addRow([
      'Por favor, rellena la columna "CANTIDAD" para añadir productos al carrito.',
    ]);
    worksheet.addRow([
      'No modifiques otras columnas para evitar errores de procesamiento, sobre todo la columna CÓDIGO.',
    ]);
    worksheet.mergeCells('A1:L1');
    worksheet.mergeCells('A2:L2');

    worksheet.getRow(1).font = { bold: true, color: { argb: 'FF0000' } };
    worksheet.getRow(2).font = { italic: true, color: { argb: 'FF0000' } };

    // TO DO - write it depending on language.
    const headers = [
      'CÓDIGO',
      'CANTIDAD',
      'DESCRIPCIÓN',
      'IDIOMA',
      'ULTIMAS UNIDADES',
      'BAJO PEDIDO',
      'UNIDAD DE NEGOCIO',
      'TIPO DE ARTÍCULO',
      'PLATAFORMA',
      'PRECIO',
      'DISPONIBILIDAD',
      'MÍNIMO DE VENTA',
    ];

    const combinedProducts = [
      ...DEFAULT_CONGRESS_PRODUCTS,
      ...products,
    ] as Product[];

    worksheet.addTable({
      name: 'ProductTable',
      ref: 'A3',
      headerRow: true,
      totalsRow: false,
      style: {
        theme: 'TableStyleMedium9',
        showRowStripes: true,
      },
      columns: headers.map((h) => ({ name: h })),
      rows: combinedProducts.map((p) => [
        p.code,
        '',
        p.nameProduct.toLowerCase(),
        p.idLanguage?.toLowerCase(),
        p.isEnabled === 'Agotar' ? 'sí' : 'no',
        p.isOnDemand ? 'sí' : 'no',
        p.businessUnit?.toLowerCase() === 'resto'
          ? 'corporativo'
          : p.businessUnit?.toLowerCase(),
        p.productType?.toLowerCase(),
        p.family?.toLowerCase(),
        `${p.price?.toFixed(2)} €`,
        p.availableWW > 0 ? 'disponible' : 'sin stock',
        p.lowest > 1 ? `min venta ${p.lowest}` : '',
      ]),
    });

    worksheet.columns.forEach((col) => {
      col.width = 20;
      if (col.header === 'CANTIDAD') {
        col.style = {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFF00' },
          },
          font: { bold: true },
        };
      }
    });

    worksheet.views = [{ state: 'frozen', ySplit: 3 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const date = new Date();
    const formattedDate = date.toISOString().slice(0, 10);
    const formattedTime = date.toTimeString().slice(0, 8).replace(/:/g, '');

    saveAs(blob, `Merchandising_${formattedDate}_${formattedTime}.xlsx`);
  }
  function downloadProductsForCongress() {
    const sortedData = sortProductsByPlatform(allProducts);
    downloadProductsForCongressExcelJS(sortedData);
  }
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { range: 2 });
      const firstRow = rows[0];
      const headers = Object.keys(firstRow || {}).map((h) =>
        h.trim().toUpperCase(),
      );

      const hasCode = headers.includes('CÓDIGO');
      const hasQuantity = headers.includes('CANTIDAD');

      if (!hasCode || !hasQuantity) {
        toast.current?.show({
          severity: 'error',
          summary: 'Archivo inválido',
          detail:
            'No se deben modificar los títulos de las columnas del archivo descargado.',
          life: 5000,
        });
        return;
      }

      rows.forEach((row: any) => {
        const code = row['CÓDIGO']?.toString().trim();
        const quantityStr = String(row['CANTIDAD'] || '').trim();

        if (
          !code ||
          quantityStr === undefined ||
          quantityStr === null ||
          quantityStr === '' ||
          parseInt(quantityStr, 10) <= 0
        ) {
          return;
        }

        const quantity = parseInt(quantityStr, 10);
        // const product = allProducts.find((p) => p.code === code);
        const product =
          allProducts.find((p) => p.code === code) ||
          (DEFAULT_CONGRESS_PRODUCTS.find((p) => p.code === code) as Product);

        if (!product || quantity <= 0) return;
        console.log('Product', product);
        resolveImageSource(code).then((imageSrc) => {
          const alreadyInCart = cartItems.some(
            (item) => item.idProduct === code,
          );

          if (alreadyInCart) {
            setQuantity(code, quantity);
          } else {
            increaseCartQuantity(
              code,
              product.nameProduct,
              product.price ?? 1,
              imageSrc,
              product.lowest ?? 1,
              product.multiple ?? 1,
            );
            setQuantity(code, quantity);
          }
        });
      });
    };

    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };
  function handleSearchQuery(query: string) {
    console.log('Search Query Changed:', query);
    setSearchQuery(query);
    getProducts(query);
  }

  return (
    <>
      {isLoading ? (
        <div className="card flex justify-content-center">
          <ProgressSpinner />
        </div>
      ) : (
        <div style={{ overflowX: 'hidden' }}>
          <DarkMenuBar />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              marginRight: '20px',
            }}
          >
            <div
              className="name-product"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginRight: '10px',
                marginTop: '12px',
                marginLeft: '1.2%',
                flex: 1,
              }}
            >
              {isDelegate || isKAM || isCongress ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i
                      className="pi pi-arrow-left"
                      style={{ marginTop: '1%', cursor: 'pointer' }}
                      onClick={() => navigate('/choiceCustomer')}
                    ></i>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: '15px',
                        lineHeight: '1.8',
                      }}
                    >
                      <div>
                        <strong>
                          {isKAM
                            ? t('banner_delegate.kam')
                            : t('banner_delegate.delegate')}
                          :
                        </strong>{' '}
                        {AuthService.GetUserEmail()}
                        {/* {AuthService.GetUserEmail() ===
                          'kelbadry@mesoestetic.com' && (
                          <span style={{ marginLeft: '5px' }}>
                            on behalf of Ivan Radtkowsky
                          </span>
                        )} */}
                      </div>
                      <div>
                        <strong>
                          {isCongress
                            ? t('banner_delegate.congress')
                            : t('banner_delegate.client')}
                          :
                        </strong>{' '}
                        {isKAM
                          ? KamService.GetKamCustomerName()
                          : DelegateService.GetDelegateName()}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '5px',
                  }}
                >
                  <strong>{t('banner_delegate.user')}:</strong>
                  <span style={{ marginLeft: '10px' }}>
                    {AuthService.GetUserEmail()}
                  </span>
                </div>
              )}
            </div>

            <div
              style={{
                position: 'relative',
                marginRight: '20px',
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <input
                type="text"
                value={searchQuery}
                className="input-search-home"
                onChange={(e) => handleSearchQuery(e.target.value)}
                style={{
                  padding: '10px',
                  paddingRight: '30px',
                  border: '1px solid #ccc',
                  width: '300px',
                  marginTop: '3.2%',
                }}
              />
              <i
                className="pi pi-search"
                style={{
                  position: 'absolute',
                  right: '5px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#ccc',
                  marginTop: '1.3%',
                }}
              ></i>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flex: 1,
              }}
            >
              <div
                className="name-product"
                style={{
                  width: '160px',
                  textAlign: 'right',
                  marginRight: '10px',
                }}
              >
                <strong>{productsCounter}</strong>
              </div>
              {isDelegate && (
                <Button
                  icon="pi pi-download"
                  onClick={() => downloadProductsToXLSX()}
                  className="p-button-outlined"
                  style={{
                    borderColor: viewMode === 'list' ? 'black' : '#C6C6C6',
                    color: viewMode === 'list' ? 'black' : '#C6C6C6',
                    backgroundColor: 'white',
                  }}
                />
              )}
              {isCongress && (
                <>
                  <Toast ref={toast} position="top-right" />
                  <Button
                    icon="pi pi-download"
                    onClick={() => downloadProductsForCongress()}
                    className="p-button-outlined"
                    style={{
                      borderColor: 'black',
                      color: 'black',
                      backgroundColor: 'white',
                    }}
                  />
                  <input
                    type="file"
                    accept=".xlsx"
                    ref={fileInputRef}
                    onChange={handleExcelUpload}
                    style={{ display: 'none' }}
                  />

                  <Button
                    icon="pi pi-upload"
                    onClick={triggerFileInput}
                    className="p-button-outlined"
                    style={{
                      borderColor: 'black',
                      color: 'black',
                      backgroundColor: 'white',
                    }}
                  />
                </>
              )}
              <Button
                icon="pi pi-table"
                onClick={() => setViewMode('grid')}
                className="p-button-outlined p-mr-2"
                style={{
                  borderColor: viewMode === 'list' ? 'black' : '#C6C6C6',
                  color: viewMode === 'list' ? 'black' : '#C6C6C6',
                  backgroundColor: 'white',
                }}
              />
              <Button
                icon="pi pi-list"
                onClick={() => setViewMode('list')}
                className="p-button-outlined"
                style={{
                  borderColor: viewMode === 'grid' ? 'black' : '#C6C6C6',
                  color: viewMode === 'grid' ? 'black' : '#C6C6C6',
                  backgroundColor: 'white',
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            <FilterSideBar />
            {viewMode === 'grid' ? (
              <div style={{ width: '100%' }}>
                <ProductCardList products={products} />
              </div>
            ) : (
              <ProductTableList products={products} filters={filters} />
            )}{' '}
          </div>
        </div>
      )}
    </>
  );
}
